import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { isFulfilled } from "@reduxjs/toolkit";

import ToggleSwitch from "components/__reusable/ToggleSwitch";
import { PicturePublishStatus, PictureType } from "config/constants";
import useAppDispatch from "hooks/useAppDispatch";
import { updatePicturePublishStatus } from "features/picture/pictureAction";
import { setErrorStatus } from "features/error/errorSlice";
import { DELETE_PICTURES_PATH } from "router/pathName";
import type { Picture } from "types/Pictures";

interface PublishStatusToggleProps {
  picture: Picture;
}

const PublishStatusToggle: React.FC<PublishStatusToggleProps> = ({
  picture,
}) => {
  const { t } = useTranslation();
  const [isPublishable, setIsPublishable] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const dispatch = useAppDispatch();
  const changePublishStatus = async (isChecked: boolean) => {
    if (isLoading) return;

    if (
      !picture.is_taged &&
      picture.picture_type !== PictureType.class &&
      isChecked
    ) {
      dispatch(
        setErrorStatus(
          "_unpublished_picture_class._no_tag_on_switch_publish_error"
        )
      );
      return;
    }

    const previousValue = isPublishable;
    setIsPublishable(isChecked);

    setIsLoading(true);
    try {
      const updateAction = await dispatch(
        updatePicturePublishStatus({
          id: picture.id,
          formData: {
            public_status: isChecked
              ? PicturePublishStatus.good
              : PicturePublishStatus.not_good,
          },
        })
      );

      if (!isFulfilled(updateAction)) {
        setIsPublishable(previousValue);
        dispatch(setErrorStatus(""));
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setIsPublishable(picture.public_status === PicturePublishStatus.good);
    setIsLoading(false);

    if (window.location.pathname.includes(DELETE_PICTURES_PATH)) {
      return;
    }

    if (
      picture.public_status === PicturePublishStatus.non_checked &&
      (picture.is_taged || picture.picture_type === PictureType.class)
    ) {
      changePublishStatus(true);
    }
  }, [picture]);

  return (
    <ToggleSwitch
      leftLabel={t("_unpublished_picture_class._unpublishable")}
      rightLabel={t("_unpublished_picture_class._publishable")}
      isChecked={isPublishable}
      isLoading={isLoading}
      onChange={changePublishStatus}
    />
  );
};

export default PublishStatusToggle;
